import React, { useCallback, useEffect, useMemo, useState } from "react";
import HighLight from "../highlight";
import SoilHealthTrendsChart from "./SoilHealthTrendsChart";
import SoilSampleAccordion from "./SoilSampleAccordion";
import useFetch from "../../hooks/useFetch";
import Loading from "../common/Loading";
import { getChartData, getLastestValueForChart } from "../../utils/helpers";
import { useMapRender } from "../../hooks/map/useMapRender";

const SoilSamplePage = () => {
  const [isLoading, setShowLoading] = useState(true);
  const [selectedSoil, setSelectedSoil] = useState();

  const { data, refresh } = useFetch("/soils");
  const { data: tractData } = useFetch("/tracts");

  useEffect(() => {
    if (data) {
      setShowLoading(false);
      if (!selectedSoil && data?.soils?.length) {
        setSelectedSoil(data.soils[0]);
      }
    }
  }, [data, selectedSoil]);

  useEffect(() => {
    if (data?.soils && selectedSoil) {
      const refreshedSelectedSoil = data.soils.find(
        (soil) => soil._id === selectedSoil._id
      );
      if (refreshedSelectedSoil) {
        setSelectedSoil(refreshedSelectedSoil);
      } else {
        setSelectedSoil(null);
      }
    }
  }, [data, selectedSoil]);

  const handleSoilClick = useCallback(
    (type, id) => {
      const newSelectedSoil = (data?.soils || []).find(
        (item) => item._id === id
      );
      if (newSelectedSoil) {
        setSelectedSoil(newSelectedSoil);
      }
    },
    [data?.soils]
  );

  const { renderMap } = useMapRender({
    tracts: tractData?.TractsDetail,
    soils: data?.soils,
    selectedItem: selectedSoil,
    mapComponent: "soil",
    extraMapProps: {
      showSoilLocationButton: true,
      refreshSoilData: refresh,
      onIconClick: handleSoilClick,
    },
  });

  const highlight = useMemo(() => {
    if (!data) {
      return null;
    }
    return [
      {
        title: "Soil Residual N",
        value: `${getLastestValueForChart(data, "soil_residual_n")} lbs / ac`,
        percentage: ((data.soil_residual_n_change || 0) * 100).toFixed(1),
        chartProps: {
          chartData: getChartData(data, "soil_residual_n"),
        },
      },
      {
        title: "Soil Residual P",
        value: `${getLastestValueForChart(data, "soil_residual_p")} lbs / ac`,
        percentage: ((data.soil_residual_p_change || 0) * 100).toFixed(1),
        chartProps: {
          chartData: getChartData(data, "soil_residual_p"),
          lineColor: "#9DC34F",
        },
      },
      {
        title: "Soil Residual K",
        value: `${getLastestValueForChart(data, "soil_residual_k")} lbs / ac`,
        percentage: ((data.soil_residual_k_change || 0) * 100).toFixed(1),
        chartProps: {
          chartData: getChartData(data, "soil_residual_k"),
          lineColor: "#396485",
        },
      },
    ];
  }, [data]);

  if (isLoading) {
    return <Loading />;
  }

  if (!tractData?.TractsDetail?.length) {
    return (
      <div className="w-full min-h-screen dark:text-white text-xl font-bold">
        No Tracts found to Add Soil Samples to…. Please contact your district
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen">
      <h2 className="font-bold text-2xl dark:text-white">
        Soil Samples Overview
      </h2>
      <div className="my-6 flex gap-x-4">
        {highlight &&
          highlight.map((item, index) => (
            <HighLight key={index} data={item} className="w-full" />
          ))}
      </div>
      <div className="w-full flex-col lg:flex-row flex items-center gap-x-5">
        <div className="w-full lg:w-3/5 h-[472px]">{renderMap()}</div>
        <div className="rounded-lg bg-white dark:bg-gray-900 dark:text-white w-full lg:flex-1 p-3 mt-4 lg:mt-0">
          <div className="flex flex-row-reverse items-center justify-between">
            <div className="font-bold text-xl">
              {selectedSoil
                ? selectedSoil.location_id
                : "Please select a soil to view the details."}
            </div>
          </div>
          <div className="mt-5 px-5 h-[400px] overflow-y-auto">
            {selectedSoil && (
              <SoilSampleAccordion selectedSoil={selectedSoil} />
            )}
          </div>
        </div>
      </div>
      {selectedSoil?.soil_samples?.length ? (
        <div className="rounded-lg bg-white dark:bg-gray-900 dark:text-white w-full mt-5 p-3">
          <div className="font-bold text-2xl">Soil Trends</div>
          <SoilHealthTrendsChart data={selectedSoil} />
        </div>
      ) : null}
    </div>
  );
};

export default SoilSamplePage;
